import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, Loader2, Printer, QrCode, RefreshCcw, ShieldCheck } from 'lucide-react';
import { Button } from '../../components/Button';
import { PageHeader } from '../../components/common/PageHeader';
import { Panel } from '../../components/common/Panel';
import { InfoItem } from '../../components/common/InfoItem';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Modal } from '../../components/common/Modal';
import { CngQrSticker } from '../../components/stickers/CngQrSticker';
import { DriverQrCard } from '../../components/stickers/DriverQrCard';
import { usePrototype } from '../../contexts/PrototypeContext';
import { getDriver } from '../../data/drivers';
import { qrStatus } from '../../utils/status';
import type { Driver } from '../../types/domain';
export function QrGeneration() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    getCng,
    setStage,
    cngList
  } = usePrototype();
  const fallback = cngList.find(c => c.stage === 4)?.id ?? 'c1';
  const cng = getCng(id ?? fallback);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(false);
  if (!cng) return <p data-id="element-3326" data-mp="3iepug6i7agwh">রেকর্ড পাওয়া যায়নি।</p>;
  const ready = cng.stage >= 5;
  const driverList = cng.driverIds.map(d => getDriver(d)).filter((d): d is Driver => Boolean(d));
  const qr = qrStatus(cng.stage);
  const generate = () => {
    if (cng.stage < 4) {
      toast.error('অনুমোদন ছাড়া QR তৈরি করা যাবে না');
      return;
    }
    setGenerating(true);
    window.setTimeout(() => {
      setGenerating(false);
      if (cng.stage === 4) setStage(cng.id, 5);
      toast.success(`QR তৈরি সম্পন্ন: ${cng.qrId} ও ${driverList.length.toLocaleString('bn-BD')}টি চালক QR`);
    }, 1100);
  };
  return <div data-id="element-3327" data-mp="exzi79w0z7mc">
      <PageHeader title="QR স্টিকার তৈরি" subtitle="অনুমোদিত সিএনজির জন্য গাড়ির QR স্টিকার ও চালকের QR কার্ড তৈরি" actions={<>
            <Button variant="outline" disabled={!ready} onClick={() => setPreview(true)} data-id="element-3329" data-mp="n0ro3ragr6h5"><Eye className="h-4 w-4" data-id="element-3330" data-mp="qy7afen2gg3n" />প্রিভিউ</Button>
            <Button variant="outline" disabled={!ready} onClick={() => {
        toast.success('পুনরায় তৈরি সম্পন্ন — পূর্বের QR বাতিল');
      }} data-id="element-3331" data-mp="vylmr8vpac7h"><RefreshCcw className="h-4 w-4" data-id="element-3332" data-mp="10x540vzcmzti" />পুনরায় তৈরি করুন</Button>
            <Button variant="outline" disabled={!ready} onClick={() => navigate('/admin/printing')} data-id="element-3333" data-mp="3ay2xw297plw"><Printer className="h-4 w-4" data-id="element-3334" data-mp="899dk90di456" />প্রিন্ট</Button>
            <Button onClick={generate} disabled={generating || ready || cng.stage < 4} data-id="element-3335" data-mp="89sk6tkkp63w">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-3336" data-mp="d7axew5mi7hp" /> : <QrCode className="h-4 w-4" data-id="element-3337" data-mp="hzu8nmgu8qjc" />}QR তৈরি করুন
            </Button>
          </>} data-id="element-3328" data-mp="ey90ebatwahh" />

      <Panel className="mb-4" data-id="element-3338" data-mp="3gpufx6yi1yzb">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3" data-id="element-3339" data-mp="3got2wwxxb2ey">
          <InfoItem label="রেজিস্ট্রেশন" value={<Link to={`/admin/cng/${cng.id}`} className="hover:underline" data-id="element-3341" data-mp="3lmktnnmu0ihk">{cng.reg}</Link>} data-id="element-3340" data-mp="3lnnk5ug8siwy" />
          <InfoItem label="মালিক" value={cng.owner.name} data-id="element-3342" data-mp="3e4e9zyzwwg6u" />
          <InfoItem label="চেসিস নং" value={cng.chassis} mono data-id="element-3343" data-mp="395a2rdwjssj5" />
          <InfoItem label="অনুমোদিত চালক" value={`${driverList.length.toLocaleString('bn-BD')} জন`} data-id="element-3344" data-mp="393qnnqmmoniy" />
          <InfoItem label="QR স্ট্যাটাস" value={<StatusBadge tone={qr.tone} data-id="element-3346" data-mp="2z85g7cysiv1t">{qr.label}</StatusBadge>} data-id="element-3345" data-mp="348jn2ronnyy9" />
          <span className="ml-auto flex items-center gap-1.5 text-xs text-slate-500" data-id="element-3347" data-mp="2z9zwuf314103"><ShieldCheck className="h-4 w-4 text-bdgreen-600" data-id="element-3348" data-mp="2v7d328mss609" />এনক্রিপ্টেড টোকেন • নকলরোধী হলোগ্রাম</span>
        </div>
      </Panel>

      <div className="grid gap-4 xl:grid-cols-[auto_1fr]" data-id="element-3349" data-mp="2lg8pndcrst7j">
        <Panel title="A. CNG VEHICLE QR STICKER" subtitle="গাড়ির সামনের কাচে (বাম দিকে) স্থাপনযোগ্য • ১০০×১৫০ মিমি" data-id="element-3350" data-mp="2lgqrcpi7bzmu">
          <div className="flex justify-center rounded-md bg-slate-50 p-6" data-id="element-3351" data-mp="2gf1hveamnu0v">
            {ready ? <CngQrSticker qrId={cng.qrId} reg={cng.reg} data-id="element-3352" data-mp="2bk5krzqjaggw" /> : <div className="flex h-[400px] w-[240px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-center text-sm text-slate-500" data-id="element-3353" data-mp="27gzjl6ia3fha">
                <QrCode className="mb-2 h-10 w-10 text-slate-300" data-id="element-3354" data-mp="3155g9wewhl5t" />
                {cng.stage < 4 ? 'অনুমোদনের অপেক্ষায়' : '“QR তৈরি করুন” চাপুন'}
              </div>}
          </div>
        </Panel>
        <Panel title="B. DRIVER QR CARD / STICKER" subtitle="প্রত্যেক অনুমোদিত চালকের জন্য আলাদা কার্ড • ৮৫×৫৪ মিমি (ID কার্ড সাইজ)" data-id="element-3355" data-mp="2reu980zx6vup">
          <div className="flex flex-wrap gap-4 rounded-md bg-slate-50 p-6" data-id="element-3356" data-mp="2diyvsaoaicyp">
            {driverList.map(d => ready ? <DriverQrCard key={d.id} driver={d} vehicleReg={cng.reg} data-id="element-3357" data-mp="24gporus8d1q2" /> : <div key={d.id} className="flex h-[150px] w-[340px] items-center justify-center rounded-xl border-2 border-dashed border-slate-300 text-sm text-slate-500" data-id="element-3358" data-mp="28grf57sim8q1">
                  {d.name} — QR তৈরি হয়নি
                </div>)}
          </div>
        </Panel>
      </div>

      {ready && cng.stage === 5 && <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm text-emerald-800" data-id="element-3359" data-mp="1r8gt9ycoaarr">
          QR তৈরি সম্পন্ন। পরবর্তী ধাপ: স্টিকার প্রিন্ট।
          <Button size="sm" onClick={() => navigate('/admin/printing')} data-id="element-3360" data-mp="1w7a6rqrym1rx"><Printer className="h-4 w-4" data-id="element-3361" data-mp="1w5qqi06azqq5" />প্রিন্ট ব্যবস্থাপনায় যান</Button>
        </div>}

      <Modal open={preview} onClose={() => setPreview(false)} title={`প্রিন্ট প্রিভিউ — ${cng.reg}`} width="max-w-4xl" data-id="element-3362" data-mp="2aumunm58ojlh">
        <div className="flex flex-wrap items-start justify-center gap-6 bg-slate-100 p-6" data-id="element-3363" data-mp="25z7gsu78enp2">
          <CngQrSticker qrId={cng.qrId} reg={cng.reg} data-id="element-3364" data-mp="2jw31xl0rc5lh" />
          <div className="space-y-4" data-id="element-3365" data-mp="2exjhxq5q53vr">{driverList.map(d => <DriverQrCard key={d.id} driver={d} vehicleReg={cng.reg} data-id="element-3366" data-mp="2evosbyjicnlc" />)}</div>
        </div>
      </Modal>
    </div>;
}